import cx from 'classnames';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useIsMobile } from '@bwoty-web/ui-kit/hooks';
import { Modal, Tab, TabList, TabPanel, TabPanels, Tabs } from '@bwoty-web/ui-kit';

import PricePanel from './PricePanel';
import PriceDetails from '../priceDetails/PriceDetails';
import FlightDetails from '../flightDetails/FlightDetails';
import BaggageDetails from '../baggageDetails/BaggageDetails';
import FlightListFlight from './FlightListFlight';
import FlightClassDetails from '../flightClassDetails/FlightClassDetails';

import * as FlightListLogUtils from '../../../utils/logUtils/FlightListLogUtils';

import './_flightListItem.scss';

const FlightListItem = ({
  addToCart = null,
  displaySettings = {},
  flightInformation,
  getFlightTime = null,
  hideBookButtonInPricePanel = false,
  hotelInformation = {},
  labels,
  searchInformation,
}) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedDetailsIndex, setSelectedDetailsIndex] = useState(0);
  const isMobile = useIsMobile();

  const { hidePriceDetails, hideFlightTime, isSummaryView } = displaySettings;
  const isSummaryViewAndMobile = isMobile && isSummaryView;

  const { outBoundFlights, homeBoundFlights } = flightInformation;
  const firstOutBoundFlight = outBoundFlights[0];
  const firstHomeBoundFlight = homeBoundFlights[0];

  const flightInfoDetailsHeading = isMobile ? labels.flightInfoDetailsHeadingShort : labels.flightInfoDetailsHeading;

  function handleDetailsClick(index) {
    switch (index) {
      case 0:
        FlightListLogUtils.flightInfoClick();
        break;
      case 1:
        FlightListLogUtils.flightClassInfoClick();
        break;
      case 2:
        FlightListLogUtils.luggageInfoClick();
        break;
      default:
        break;
    }
    return FlightListLogUtils.priceInfoClick();
  }

  const openDetails = (index) => {
    setIsDetailsOpen(true);
    setSelectedDetailsIndex(index);
    handleDetailsClick(index);
    // If flight time information has not been fetched, call the API to fetch it.
    if (!flightInformation.flightTimeInformation && getFlightTime) {
      getFlightTime(firstHomeBoundFlight.bookingEngineKey, firstOutBoundFlight.bookingEngineKey, flightInformation.id);
    }
  };

  return (
    <div
      className={cx('iw-flight-list-item', {
        'iw-flight-list-item--isSummaryView': isSummaryView,
        'iw-flight-list-item--isSummaryViewAndMobile': isSummaryViewAndMobile,
      })}
    >
      <div className="iw-flight-list-item__flight-info">
        {outBoundFlights.map((outBoundFlight) => {
          return (
            <FlightListFlight
              // **Keyed by the leg, not the booking.** `bookingEngineKey` identifies the whole
              // transport, so every leg of one journey carries the same value — a stopover
              // outbound is two legs and React saw duplicate keys. The two lists are also
              // siblings in this one parent, so React flattens them together and the keys have
              // to be unique across the pair, hence the prefix as well.
              key={`outbound-${outBoundFlight.departureLocationCode}-${outBoundFlight.destinationCode}-${outBoundFlight.departureTime}`}
              flightInfo={outBoundFlight}
              labels={labels}
              openDetails={(index) => openDetails(index)}
              hideFlightTime={hideFlightTime}
              isSummaryView={isSummaryView}
            />
          );
        })}

        {homeBoundFlights.map((homeBoundFlight) => {
          return (
            <FlightListFlight
              key={`homebound-${homeBoundFlight.departureLocationCode}-${homeBoundFlight.destinationCode}-${homeBoundFlight.departureTime}`}
              flightInfo={homeBoundFlight}
              labels={labels}
              openDetails={(index) => openDetails(index)}
              hideFlightTime={hideFlightTime}
              isSummaryView={isSummaryView}
              isHomeBound
            />
          );
        })}
      </div>
      <div className="iw-flight-list-item__price-info">
        <PricePanel
          addToCart={addToCart}
          displaySettings={displaySettings}
          flightInformation={flightInformation}
          hideBookButton={hideBookButtonInPricePanel}
          labels={labels}
          openDetails={(index) => openDetails(index)}
          searchInformation={searchInformation}
        />
      </div>
      <Modal
        heading={labels.detailsHeading}
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
      >
        <Tabs
          onChange={(index) => setSelectedDetailsIndex(index)}
          selectedIndex={selectedDetailsIndex}
        >
          <TabList
            style={{ marginBottom: 32 }}
            fullWidth
          >
            <Tab>{flightInfoDetailsHeading}</Tab>
            <Tab>{labels.flightClassDetailsHeading}</Tab>
            <Tab>{labels.luggageDetailsHeading}</Tab>
            {!hidePriceDetails && <Tab>{labels.priceDetailsHeading}</Tab>}
          </TabList>
          <TabPanels>
            <TabPanel>
              <FlightDetails
                labels={labels}
                flights={[...outBoundFlights, ...homeBoundFlights]}
                flightTimeInformation={flightInformation.flightTimeInformation}
                hideFlightTime={hideFlightTime}
              />
            </TabPanel>
            <TabPanel>
              <FlightClassDetails
                labels={labels}
                cabinClass={flightInformation.cabinClass} // var kommer denna ifrån, kan ev vara olika för alla.
                flights={[...outBoundFlights, ...homeBoundFlights]}
              />
            </TabPanel>
            <TabPanel>
              <BaggageDetails
                labels={labels}
                isIncluded={flightInformation.luggageIncluded}
                flights={[...outBoundFlights, ...homeBoundFlights]}
              />
            </TabPanel>
            {!hidePriceDetails && (
              <TabPanel>
                <div className="iw-flight-list-item__price-details">
                  <PriceDetails
                    labels={labels.priceDetails}
                    hotelInformation={hotelInformation}
                    outBoundInformation={firstOutBoundFlight}
                    priceBreakDown={flightInformation.priceBreakDown}
                    searchInformation={searchInformation}
                    showFlightTaxDescription
                    isSummaryView={isSummaryView}
                  />
                </div>
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      </Modal>
    </div>
  );
};

FlightListItem.propTypes = {
  flightInformation: PropTypes.shape({
    id: PropTypes.string,
    outBoundFlights: PropTypes.arrayOf(
      PropTypes.shape({
        bookingEngineKey: PropTypes.string,
      }).isRequired,
    ),
    homeBoundFlights: PropTypes.arrayOf(
      PropTypes.shape({
        bookingEngineKey: PropTypes.string,
      }).isRequired,
    ),
    cabinClass: PropTypes.string,
    priceBreakDown: PropTypes.shape({}),
    luggageIncluded: PropTypes.bool,
    carrierName: PropTypes.string,
    flightTimeInformation: PropTypes.shape({}),
  }).isRequired,
  hotelInformation: PropTypes.shape({
    roomGroup: PropTypes.shape({}),
  }),
  searchInformation: PropTypes.shape({}).isRequired,
  labels: PropTypes.shape({
    detailsHeading: PropTypes.string,
    flightInfoDetailsHeading: PropTypes.string,
    flightInfoDetailsHeadingShort: PropTypes.string,
    flightClassDetailsHeading: PropTypes.string,
    priceDetailsHeading: PropTypes.string,
    luggageDetailsHeading: PropTypes.string,
    priceDetails: PropTypes.shape(),
  }).isRequired,
  getFlightTime: PropTypes.func,
  addToCart: PropTypes.func,
  displaySettings: PropTypes.shape({
    hideFlightTime: PropTypes.bool,
    hidePriceDetails: PropTypes.bool,
    isSummaryView: PropTypes.bool,
  }),
  hideBookButtonInPricePanel: PropTypes.bool,
};

export default FlightListItem;
